import type { FileTypeDefinition } from "./experience";
import type { LayoutDocument } from "./layouts";
import type { WorldDocument, JsonValue } from "./types";
import { normalizeLayoutDocument } from "./layouts";

export interface GenerateLayoutAiPromptOptions {
  fileType: FileTypeDefinition;
  userRequest: string;
  currentLayout?: LayoutDocument;
  sampleCard?: WorldDocument | Record<string, JsonValue>;
}

export interface ParseLayoutAiResponseResult {
  success: boolean;
  layout?: LayoutDocument;
  error?: string;
  summary?: string;
}

const COMPONENTS_CATALOG = `
- Separator: { orientation?: "horizontal" | "vertical", className?: string }
- Accordion: { title: string, defaultOpen?: boolean, children: string[] }
- Collapsible: { title: string, defaultOpen?: boolean, children: string[] }
- Avatar: { src: string | { $state: string }, size?: "sm" | "md" | "lg" | "xl", shape?: "circle" | "rounded" | "square" }
- Alert: { title?: string, description?: string | { $state: string }, variant?: "info" | "warning" | "error" | "success" }
- Table: { headers: string[], rows: (string | { $state: string })[][] }
- Image: { src: string | { $state: string }, aspectRatio?: "square" | "video" | "portrait" | "wide", objectFit?: "cover" | "contain", rounded?: "none" | "md" | "lg" | "xl" | "full" }
- Stack: { direction?: "horizontal" | "vertical", gap?: "none" | "xs" | "sm" | "md" | "lg" | "xl", align?: "start" | "center" | "end" | "stretch", justify?: "start" | "center" | "end" | "between", wrap?: boolean, bgColor?: string, borderColor?: string, rounded?: string, className?: string, children: string[] }
- Grid: { columns?: number, gap?: "none" | "xs" | "sm" | "md" | "lg" | "xl", bgColor?: string, rounded?: string, className?: string, children: string[] }
- Card: { title?: string | { $state: string }, description?: string | { $state: string }, rounded?: "none" | "md" | "lg" | "xl" | "2xl", bgColor?: string, borderColor?: string, className?: string, children: string[] }
- Heading: { text: string | { $state: string }, level?: 1 | 2 | 3 | 4, color?: string, className?: string }
- Text: { text: string | { $state: string }, variant?: "body" | "muted" | "caption" | "lead", color?: string, className?: string }
- Badge: { text: string | { $state: string }, color?: string, variant?: "solid" | "outline" | "subtle", className?: string }
- Progress: { value: number | { $state: string }, max?: number | { $state: string }, maxBinding?: string, color?: string, showValue?: boolean, stepper?: boolean, editPath?: { value: string } }
- Tabs: { tabs: { label: string, value: string }[], defaultValue?: string, bgColor?: string, className?: string, children: string[] }
- Checkbox: { label: string, checked?: boolean | { $state: string }, editPath?: { value: string } }
- Switch: { label: string, checked?: boolean | { $state: string }, editPath?: { value: string } }
- List: { items: (string | { $state: string })[], ordered?: boolean }
- Callout: { title?: string, text: string | { $state: string }, icon?: string, color?: string, bgColor?: string, variant?: "panel" | "subtle" }
- ReferenceValue: { source: string | { $state: string }, field: string, emptyFallback?: string }
- Markdown: { content: string | { $state: string }, color?: string, className?: string }
- StatGrid: { columns?: number, items?: { label: string, value: string | number | { $state: string } }[], size?: "sm" | "md" | "lg" }
- DerivedStatGrid: { columns?: number, items?: { label: string, value: string | number | { $state: string }, formula?: string }[] }
- BadgeList: { items: string[] | { $state: string }, color?: string, variant?: "outline" | "solid" | "subtle" }
- Checklist: { items: { text: string, done: boolean | { $state: string } }[] }
- Include: { section: string }
- SectionSwitch: { statePath: string, cases: { equals: unknown, section: string }[], default?: string }
- Dots: { value: number | { $state: string }, max: number, color?: string, showValue?: boolean, editPath?: { value: string } }
- Stepper: { value: number | { $state: string }, min?: number, max?: number, step?: number, editPath?: { value: string } }
- Toggle: { value: boolean | { $state: string }, label?: string, editPath?: { value: string } }
- TrackBoxes: { value: number | { $state: string }, max: number, label?: string, editPath?: { value: string } }
- ConditionToggles: { conditions?: { id: string, label: string, active: boolean | { $state: string } }[], size?: "sm" | "md" }
- Clock: { segments?: number, current?: number | { $state: string }, label?: string }
- DataTable: { items: unknown[] | { $state: string }, columns: { field: string, label: string }[] }
- FieldList: { items?: { label: string, value: unknown | { $state: string } }[] }
- ImageHeader: { src: string | { $state: string }, title?: string | { $state: string }, subtitle?: string | { $state: string }, aspectRatio?: "portrait" | "video" | "square" | "ultrawide", overlay?: "gradient" | "solid" | "none" }
- FormattedDate: { date: string | { $state: string }, format?: "short" | "long" | "relative" }
- FormattedNumber: { value: number | { $state: string }, prefix?: string, suffix?: string }
- Select: { options: { label: string, value: string }[], value?: string | { $state: string }, editPath?: { value: string } }
- VoiceSelect: { value?: string | { $state: string }, editPath?: { value: string } }
- FileReferenceGrid: { items: unknown[] | { $state: string }, columns?: number, imageAspectRatio?: "portrait" | "video" | "square" }
- PageReferenceGrid: { items: unknown[] | { $state: string }, columns?: number }
- TurnTracker: { currentRound?: number | { $state: string }, activeTurnIndex?: number | { $state: string } }
`.trim();

/**
 * Builds a prompt for External AIs (ChatGPT, Claude, DeepSeek, etc.) matching the layout specification format.
 */
export function generateLayoutAiPrompt(options: GenerateLayoutAiPromptOptions): string {
  const { fileType, userRequest, currentLayout, sampleCard } = options;

  // Build JSON Schema representation from FileTypeDefinition fields
  const properties: Record<string, Record<string, unknown>> = {};
  for (const field of fileType.fields) {
    const anyField = field as unknown as Record<string, unknown>;
    const prop: Record<string, unknown> = {
      title: field.label,
    };
    if (field.type === "number") {
      prop.type = "number";
      const defVal = field.defaultValue ?? anyField.default;
      if (typeof defVal === "number") prop.default = defVal;
      if (typeof anyField.min === "number") prop.minimum = anyField.min;
      if (typeof anyField.max === "number") prop.maximum = anyField.max;
    } else if (field.type === "boolean") {
      prop.type = "boolean";
      const defVal = field.defaultValue ?? anyField.default;
      if (typeof defVal === "boolean") prop.default = defVal;
    } else if (field.type === "long_text") {
      prop.type = "string";
      prop.format = "textarea";
    } else if (field.type === "enum" || (anyField.type as string) === "select" || (anyField.type as string) === "radio") {
      prop.type = "string";
      if (field.options?.length) {
        prop.enum = (field.options as unknown[]).map((o) => {
          if (typeof o === "string") return o;
          const optObj = o as { label?: string; value?: string };
          return optObj.label || optObj.value;
        });
      }
    } else if (field.type === "list" || (anyField.type as string) === "multi_select") {
      prop.type = "array";
      prop.items = { type: "string" };
      if (field.options?.length) {
        prop.items = {
          enum: (field.options as unknown[]).map((o) => {
            if (typeof o === "string") return o;
            const optObj = o as { label?: string; value?: string };
            return optObj.label || optObj.value;
          }),
        };
      }
    } else if (field.type === "reference") {
      prop.type = "string";
      prop.format = "reference";
      if (anyField.referenceTypeId) prop.referenceType = anyField.referenceTypeId;
    } else {
      prop.type = "string";
    }
    if (anyField.helpText || anyField.description) {
      prop.description = anyField.helpText || anyField.description;
    }
    properties[field.id] = prop;
  }

  const dataSchemaJson = JSON.stringify({
    type: "object",
    properties,
  }, null, 2);

  // Normalize current layout to spec format
  let specJson = "{}";
  if (currentLayout) {
    const normalized = normalizeLayoutDocument(currentLayout);
    specJson = JSON.stringify(normalized, null, 2);
  }

  // Sample file content
  let sampleJson = "{}";
  if (sampleCard) {
    const cardObj = sampleCard as Record<string, unknown>;
    if ("fields" in cardObj && cardObj.fields && typeof cardObj.fields === "object") {
      sampleJson = JSON.stringify({
        title: cardObj.title,
        summary: cardObj.summary,
        ...(cardObj.fields as Record<string, unknown>),
      }, null, 2);
    } else {
      sampleJson = JSON.stringify(sampleCard, null, 2);
    }
  }

  return `You are editing a JSON layout specification. The specification describes how a structured document renders as an interactive card or detail sheet.

Use the catalog below — every component, prop name, and enum value is exhaustive. Do not invent components or props that are not listed. Bind dynamic values to file fields using $state references that match the file type's data schema.

## Spec format
- root: string — id of the root element
- elements: { [id]: Element }
- Element: { type: ComponentType, props?: object, visible?: Condition, repeat?: { statePath, key? }, children?: string[] }
- Binding: { "$state": "/path/to/file/field" } resolves the value at that path in the file content. Always-available paths: "/title", "/name", "/description", "/image", "/createdAt", "/updatedAt". Other paths must come from the file type schema below.
- Condition: boolean, a single condition ({ "$state": "/path" }, { "$item": "field" }, or { "$index": true } plus at most one of eq/neq/gt/gte/lt/lte and optional not: true), an array of single conditions (AND), or { "$and": [...] } / { "$or": [...] } wrappers.
- repeat: { "statePath": "/arrayField", "key"?: "itemField" } renders the element's children once per item of the bound array. Inside a repeat, bind props with { "$item": "field" } (or "" for the whole item) and { "$index": true }, and use $item/$index conditions for per-row visibility. repeat statePaths are absolute — nested repeats are not supported.
- sections (optional, top-level): { "<section-id>": { "name"?: string, "root": string, "elements": { ... } } } — named self-contained fragments. Mount one with an Include element ({ "section": "<id>" }, combine with visible for conditional sections) or pick one of several with SectionSwitch ({ "statePath", "cases": [{ "equals", "section" }], "default"? }).
- editPath: For interactive controls (Stepper, Dots, Toggle, Progress, Select), specify { "editPath": { "value": "/path/to/targetField" } } so user clicks directly modify the file.
- Every component accepts an optional className prop of extra utility classes applied to its root (e.g. "col-span-2" inside a Grid, "mt-4", "max-w-sm", "shadow-lg"). Prefer dedicated props (color, size, gap, …) when one exists; use className for layout tweaks they cannot express.

## Catalog
${COMPONENTS_CATALOG}

## Target file type
- name: ${fileType.name}
- designation: ${fileType.designation}

Data schema (JSON Schema for the file's content):
\`\`\`json
${dataSchemaJson}
\`\`\`

Example file:
\`\`\`json
${sampleJson}
\`\`\`

Current spec:
\`\`\`json
${specJson}
\`\`\`

## Requested changes
${userRequest}

## Reply format
Reply with the full updated JSON spec inside a single \`\`\`json fenced block. Preserve all existing $state bindings unless explicitly told to change them. Do not include any explanatory text outside the code block.`;
}

/**
 * Parses the response from an external AI model into a valid LayoutDocument.
 */
export function parseLayoutAiResponse(rawAiResponse: string): ParseLayoutAiResponseResult {
  if (!rawAiResponse || typeof rawAiResponse !== "string") {
    return { success: false, error: "Empty response received." };
  }

  // Extract JSON from markdown fenced block or raw JSON string
  let jsonString = rawAiResponse.trim();
  const jsonBlockRegex = /```(?:json)?\s*([\s\S]*?)\s*```/i;
  const match = jsonBlockRegex.exec(rawAiResponse);
  if (match && match[1]) {
    jsonString = match[1].trim();
  }

  // Handle potential leading/trailing garbage
  const firstBrace = jsonString.indexOf("{");
  const lastBrace = jsonString.lastIndexOf("}");
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    jsonString = jsonString.slice(firstBrace, lastBrace + 1);
  }

  try {
    const parsed = JSON.parse(jsonString);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { success: false, error: "Parsed JSON is not an object." };
    }

    if (!parsed.root || typeof parsed.root !== "string") {
      return { success: false, error: "Layout spec is missing a root element id." };
    }

    if (!parsed.elements || typeof parsed.elements !== "object") {
      return { success: false, error: "Layout spec is missing an elements map." };
    }

    const normalized = normalizeLayoutDocument(parsed);
    return {
      success: true,
      layout: normalized,
      summary: `Parsed layout with root "${normalized.root}", ${Object.keys(normalized.elements).length} elements, and ${Object.keys(normalized.sections ?? {}).length} sections.`,
    };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    return { success: false, error: `Invalid JSON format: ${msg}` };
  }
}
